// Lightweight client for the auth API.
// Token survives reload via localStorage; cookies are sent too via credentials: 'include'.
const API = process.env.REACT_APP_BACKEND_URL;
const STORAGE_KEY = 'fls_access_token';

export const getToken = () => localStorage.getItem(STORAGE_KEY) || '';
export const setToken = (t) => {
  if (t) localStorage.setItem(STORAGE_KEY, t);
  else localStorage.removeItem(STORAGE_KEY);
};

/**
 * Clone-safe response body reader. Avoids the
 * "Failed to execute 'clone' on 'Response': Response body is already used"
 * race when analytics scripts (PostHog/rrweb/etc.) try to clone() the same
 * response after we've consumed it. By cloning OURSELVES first and reading
 * text from the clone, the original body is left intact for interceptors.
 */
export async function readJSON(res) {
  try {
    const cloned = res.clone();
    const text = await cloned.text();
    if (!text) return {};
    return JSON.parse(text);
  } catch {
    return {};
  }
}

export async function authFetch(path, options = {}) {
  const headers = { 'Content-Type': 'application/json', ...(options.headers || {}) };
  const token = getToken();
  if (token) headers['Authorization'] = `Bearer ${token}`;
  const res = await fetch(`${API}${path}`, {
    ...options,
    headers,
    credentials: 'include',
  });
  return res;
}

async function parseError(res) {
  const data = await readJSON(res);
  const d = data?.detail;
  if (typeof d === 'string') return { message: d };
  if (Array.isArray(d)) return { message: d.map((e) => e?.msg || JSON.stringify(e)).join(' ') };
  if (d && typeof d === 'object') return { message: d.message || `${res.status} ${res.statusText}`, code: d.code };
  return { message: data?.message || `${res.status} ${res.statusText}` };
}

function throwApiError(parsed) {
  const err = new Error(parsed.message);
  if (parsed.code) err.code = parsed.code;
  throw err;
}

export async function register({ email, password, name }) {
  const res = await authFetch('/api/auth/register', {
    method: 'POST',
    body: JSON.stringify({ email, password, name }),
  });
  if (!res.ok) throwApiError(await parseError(res));
  const data = await readJSON(res);
  if (data.access_token) setToken(data.access_token);
  return data.user;
}

export async function login({ email, password }) {
  const res = await authFetch('/api/auth/login', {
    method: 'POST',
    body: JSON.stringify({ email, password }),
  });
  if (!res.ok) throwApiError(await parseError(res));
  const data = await readJSON(res);
  if (data.access_token) setToken(data.access_token);
  return data.user;
}

export async function exchangeGoogleSession(session_id) {
  const res = await authFetch('/api/auth/google/callback', {
    method: 'POST',
    body: JSON.stringify({ session_id }),
  });
  if (!res.ok) throwApiError(await parseError(res));
  const data = await readJSON(res);
  if (data.access_token) setToken(data.access_token);
  return data.user;
}

export async function fetchMe() {
  const res = await authFetch('/api/auth/me', { method: 'GET' });
  if (!res.ok) {
    if (res.status === 401) return null;
    throwApiError(await parseError(res));
  }
  const data = await readJSON(res);
  return data.user;
}

export async function logout() {
  try {
    await authFetch('/api/auth/logout', { method: 'POST' });
  } finally {
    setToken('');
  }
}
